import React, { useState, useRef } from 'react';
import { FiPhone, FiCheck, FiX, FiUsers } from 'react-icons/fi';

const RecipientInput = ({ recipients = [], setRecipients }) => {
  const [inputValue, setInputValue] = useState('');
  const [isValidNumber, setIsValidNumber] = useState(true);
  const inputRef = useRef(null);

  const validatePhoneNumber = (number) => {
    // Check for spaces first
    if (number.includes(' ')) {
      return { isValid: false, hasSpaces: true };
    }
    
    // Remove all non-digits
    const cleanNumber = number.replace(/\D/g, '');
    
    // Check if it's exactly 10 digits
    if (cleanNumber.length === 9) {
      return { isValid: true, hasSpaces: false };
    }
    
    return { isValid: false, hasSpaces: false };
  };

  // const formatPhoneNumber = (number) => {
  //   // Remove all non-digits
  //   const cleanNumber = number.replace(/\D/g, '');
    
  //   // Limit to 10 digits
  //   const limitedNumber = cleanNumber.slice(0, 10);
    
  //   // Format with spaces for display: +234 XXX XXX XXXX
  //   if (limitedNumber.length <= 3) {
  //     return `+234 ${limitedNumber}`;
  //   } else if (limitedNumber.length <= 6) {
  //     return `+234 ${limitedNumber.slice(0, 3)} ${limitedNumber.slice(3)}`;
  //   } else {
  //     return `+234 ${limitedNumber.slice(0, 3)} ${limitedNumber.slice(3, 6)} ${limitedNumber.slice(6)}`;
  //   }
  // };

  const addRecipient = () => {
    if (inputValue.trim()) {
      // Remove spaces from input before validation
      const cleanInput = inputValue.replace(/\s/g, '');
      const validation = validatePhoneNumber(cleanInput);
      
      if (validation.isValid) {
        const cleanNumber = cleanInput.replace(/\D/g, '').slice(0, 9);
        // Add 233 prefix for the payload
        const formattedNumber = '233' + cleanNumber;
        
        if (!recipients.includes(formattedNumber)) {
          setRecipients([...recipients, formattedNumber]);
          setInputValue('');
          setIsValidNumber(true);
        } else {
          setIsValidNumber(false);
          // Show specific error for duplicate
          setTimeout(() => {
            setIsValidNumber(true);
          }, 3000);
        }
      } else {
        setIsValidNumber(false);
        // Show specific error for spaces
        if (validation.hasSpaces) {
          // You could add a toast or specific error message here
          //console.log('Phone number contains spaces');
        }
      }
    }
  };

  const removeRecipient = (index) => {
    setRecipients(recipients.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addRecipient();
    }
  };



  const handleInputChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    
    // Remove spaces and only allow digits, limit to 10 characters
    const digitsOnly = value.replace(/\s/g, '').replace(/\D/g, '').slice(0, 9);
    
    // Calculate new cursor position based on digits before cursor
    const digitsBeforeCursor = value.slice(0, cursorPos).replace(/\s/g, '').replace(/\D/g, '').length;
    const newCursorPos = Math.min(digitsBeforeCursor, digitsOnly.length);
    
    setInputValue(digitsOnly);
    setIsValidNumber(true);
    
    // Set cursor position after state update
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };



  const clearAllRecipients = () => {
    setRecipients([]);
  };

  return (
    <div className="p-6 mb-6 bg-white rounded-2xl border border-gray-100 shadow-xl">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-[#E5EAFF] border border-[#C4D7E1] rounded-xl">
            <FiUsers className="text-xl text-[#6C86FF]" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800">Recipients</h3>
            <p className="text-sm text-gray-600">Add phone numbers to send messages</p>
          </div>
        </div>
        
        {recipients.length > 0 && (
          <button
            onClick={clearAllRecipients}
            className="flex items-center space-x-1 text-sm font-medium text-red-500 transition-colors hover:text-red-700"
          >
            <FiX className="text-lg" />
            <span>Clear All</span>
          </button>
        )}
      </div>

      {/* Input Section */}
      <div className="mb-6">
        <div className={`relative flex items-center bg-gray-50 rounded-xl border-2 transition-all ${
          isValidNumber ? 'border-gray-200 focus-within:border-red-500' : 'border-red-300 focus-within:border-red-500'
        }`}>
          <FiPhone className={`ml-4 text-lg ${isValidNumber ? 'text-gray-400' : 'text-red-400'}`} />
          <div className="flex flex-1 items-center min-w-0">
            <span className="flex-shrink-0 px-2 py-4 font-medium text-gray-500 border-r border-gray-200">+233</span>
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder="244756782"
              className="flex-1 px-4 py-4 min-w-0 placeholder-gray-400 text-gray-700 bg-transparent outline-none"
            />
          </div>
          <button
            onClick={addRecipient}
            disabled={!inputValue.trim()}
            className="flex flex-shrink-0 justify-center items-center px-3 py-4 ml-2 font-medium text-white bg-[#ee3636] rounded-lg transition-all transform sm:ml-4 sm:px-6 hover:from-blue-600 hover:to-purple-700 disabled:from-gray-300 disabled:to-gray-400 hover:scale-105 disabled:transform-none"
          >
            <span className="hidden sm:inline">Add Number</span>
            <FiCheck className="text-lg sm:hidden" />
          </button>
        </div>
        
        {!isValidNumber && (
          <p className="mt-2 ml-2 text-sm text-red-500">
            {recipients.includes('233' + inputValue.replace(/\D/g, '').slice(0, 9)) 
              ? 'This number is already added' 
              : 'Please enter exactly 9 digits (e.g., 5512345678)'}
          </p>
        )}
        
        <div className="mt-3">
          <p className="text-sm text-gray-500">
            Enter a number and click "Add Number" or press Enter to add it to the list
          </p>
          <p className="mt-1 text-xs text-gray-400">
            Enter 9 digits only after the +233prefix
          </p>
        </div>
      </div>

      {/* Recipients Display */}
      {recipients.length > 0 && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold text-gray-700">
              Added Recipients ({recipients.length})
            </h4>
          </div>
          
          <div className="overflow-y-auto max-h-64 custom-scrollbar">
            <div className="grid gap-2">
              {recipients.map((number, index) => (
                <div
                  key={index}
                  className="flex justify-between items-center p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100 transition-all hover:shadow-md"
                >
                  <div className="flex items-center space-x-3">
                    <div className="p-1 bg-blue-100 rounded-full">
                      <FiPhone className="text-sm text-blue-600" />
                    </div>
                    <span className="font-medium text-gray-700">{number}</span>
                  </div>
                  <button
                    onClick={() => removeRecipient(index)}
                    className="p-1 text-red-400 rounded-full transition-all hover:text-red-600 hover:bg-red-50"
                  >
                    <FiX className="text-lg" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {recipients.length === 0 && (
        <div className="py-12 text-center rounded-xl border-2 border-gray-200 border-dashed">
          <FiUsers className="mx-auto mb-4 text-4xl text-gray-300" />
          <p className="mb-2 text-lg text-gray-500">No recipients added yet</p>
          <p className="text-sm text-gray-400">Start by adding phone numbers above</p>
        </div>
      )}
    </div>
  );
};

export default RecipientInput; 